/**
 * apps/web/src/components/RouteDrawer.tsx
 *
 * A wide right-side drawer that hosts a DETAIL ROUTE rendered over its list (via
 * the background-location pattern in App.tsx). Distinct from SlideOver (the
 * narrow create/edit panel): this is a max-w-3xl reading surface at z-40, so the
 * detail page's own create/edit SlideOvers (z-50) still layer above it.
 *
 * Closing navigates back to the list (the background entry). A hard-refresh /
 * deep-link to the detail URL bypasses this entirely and renders the full page
 * (App.tsx falls through to the non-drawer route).
 */

import type React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useStrings } from '../i18n';

export function RouteDrawer({ children }: { children: React.ReactNode }): React.ReactElement {
  const strings = useStrings();
  const navigate = useNavigate();

  function close(): void {
    navigate(-1);
  }

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') navigate(-1);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <div
      className="fixed inset-0 z-40"
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> only becomes modal
      // via the imperative showModal() call and brings its own ::backdrop and focus
      // handling, which would conflict with the z-40/z-50 layering this drawer relies on.
      role="dialog"
      aria-modal="true"
      aria-label={strings.nav.clienti}
    >
      <div
        className="absolute inset-0 bg-backdrop backdrop-blur-sm"
        onMouseDown={close}
        aria-hidden="true"
      />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-3xl flex-col bg-overlay text-overlay-foreground shadow-[var(--overlay-shadow)]">
        <div className="flex items-center justify-end border-b border-border px-4 py-3">
          <button
            type="button"
            onClick={close}
            aria-label={strings.common.cancel}
            className="rounded-lg p-2 text-muted transition-colors hover:bg-surface-hover hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-5 md:px-6">{children}</div>
      </div>
    </div>
  );
}
